import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableRow
} from "@material-ui/core";
import { useUser } from "@saleor/auth";
import { DateTime } from "@saleor/components/Date";
import MyTypography from "@saleor/components/MyTypography";
import Skeleton from "@saleor/components/Skeleton";
import StickyHorizontalScrollBar from "@saleor/components/StickyHorizontalScrollBar";
import TableCellHeader from "@saleor/components/TableCellHeader";
import TablePaginationCustom from "@saleor/components/TablePaginationCustom";
import NotFoundContract from "@saleor/createOrder/components/NotFoundContract";
import { ContractListUrlSortField } from "@saleor/createOrder/urls";
import { useCustomerBlockContext } from "@saleor/customerBlock/hook/useCustomerBlockContext";
import {
  CustomerContractsQuery,
  useGetCreditLimitLazyQuery
} from "@saleor/graphql";
import { makeStyles } from "@saleor/macaw-ui";
import { maybe, renderCollection } from "@saleor/misc";
import { ListProps, RelayToFlat, SortPage } from "@saleor/types";
import { getArrowDirection } from "@saleor/utils/sort";
import React, { useRef, useState } from "react";
import { FormattedMessage } from "react-intl";

const useStyles = makeStyles(
  theme => ({
    wrapper: {
      background: "rgba(212, 244, 248, 0.8)"
    },
    root: {
      overflowX: "auto",
      width: "100%",
      "&::-webkit-scrollbar": {
        height: "14px",
        background: "#FFFFFF"
      },
      "&::-webkit-scrollbar-track": {
        borderRadius: 10
      },
      "&::-webkit-scrollbar-thumb": {
        background: "#CCCCCC",
        borderRadius: 10,
        border: "4px solid #FFFFFF",
        height: "6px"
      },
      "& .MuiTableCell-head": {
        "& *": {
          color: "#696969"
        }
      }
    },
    cursorPointer: {
      cursor: "pointer"
    },
    colBU: {
      color: theme.palette.grey[900]
    },
    colCompany: {
      whiteSpace: "nowrap",
      color: theme.palette.grey[900]
    },
    colContractNo: {
      "& .MuiTypography-root": {
        color: theme.palette.info.main
      }
    },
    colProjectName: {
      whiteSpace: "nowrap",
      color: theme.palette.grey[800]
    },
    colDate: {
      color: theme.palette.grey[800]
    },
    colPayment: {
      color: theme.palette.grey[800],
      whiteSpace: "nowrap"
    },
    link: {
      cursor: "pointer"
    },
    typo: {
      marginBottom: "1.6rem"
    },
    noFilteredContract: {
      fontFamily: "inherit",
      fontStyle: "italic",
      letterSpacing: 0,
      color: theme.palette.info.dark
    }
  }),
  { name: "SoldToList" }
);

const numberOfColumns = 7;

interface SoldToListProps
  extends ListProps,
    SortPage<ContractListUrlSortField> {
  customerContracts: RelayToFlat<
    CustomerContractsQuery["scgpCustomerContracts"]
  >;
  soldToExists?: boolean;
  noFilterInput?: boolean;
  totalCount: number | null;
  onViewContract: (id: string) => void;
}

const SoldToList: React.FC<SoldToListProps> = props => {
  const {
    disabled,
    settings,
    customerContracts,
    noFilterInput,
    pageInfo,
    sort,
    totalCount,
    onPreviousPage,
    onNextPage,
    onUpdateListSettings,
    onViewContract,
    onSort
  } = props;
  const classes = useStyles(props);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [selectedRowId, setSelectedRowId] = useState<string>("");
  const { company, user } = useUser();
  const { onOpenCreditBlockDialog } = useCustomerBlockContext();
  const isStaffRole = user?.isStaff;

  const [getCreditLimitLazy] = useGetCreditLimitLazyQuery({
    onCompleted: data => {
      if (data?.getCreditLimit?.creditBlockStatus) {
        onOpenCreditBlockDialog();
      } else {
        onViewContract(selectedRowId);
      }
    }
  });

  const onTableRowClick = data => {
    getCreditLimitLazy({
      variables: {
        input: {
          soldToCode: company,
          contractNo: data.code
        }
      }
    });

    setSelectedRowId(data?.id);
  };

  return (
    <>
      <MyTypography variant="h7" className={classes.typo}>
        รายการที่เกี่ยวข้อง
      </MyTypography>
      <div className={classes.root} ref={containerRef}>
        {customerContracts?.length || noFilterInput || disabled ? (
          <Table>
            <TableHead>
              <TableRow>
                {isStaffRole && (
                  <TableCellHeader className={classes.colBU}>
                    <FormattedMessage defaultMessage="BU" />
                  </TableCellHeader>
                )}
                <TableCellHeader className={classes.colCompany}>
                  <FormattedMessage defaultMessage="Company" />
                </TableCellHeader>
                <TableCellHeader
                  className={classes.colContractNo}
                  direction={
                    sort.sort === ContractListUrlSortField.contractNo
                      ? getArrowDirection(sort.asc)
                      : undefined
                  }
                  arrowPosition="right"
                  onClick={() => onSort(ContractListUrlSortField.contractNo)}
                >
                  <FormattedMessage defaultMessage="เลขที่ Contract" />
                </TableCellHeader>
                <TableCellHeader className={classes.colProjectName}>
                  <FormattedMessage defaultMessage="ชื่อโครงการ" />
                </TableCellHeader>
                <TableCellHeader className={classes.colDate}>
                  <FormattedMessage defaultMessage="วันที่เริ่มต้น" />
                </TableCellHeader>
                <TableCellHeader className={classes.colDate}>
                  <FormattedMessage defaultMessage="วันที่สิ้นสุด" />
                </TableCellHeader>
                <TableCellHeader className={classes.colPayment}>
                  <FormattedMessage defaultMessage="เงื่อนไขการชำระเงิน" />
                </TableCellHeader>
              </TableRow>
            </TableHead>
            <TableFooter>
              <TableRow>
                <TableCell
                  colSpan={numberOfColumns}
                  className={classes.wrapper}
                >
                  <TablePaginationCustom
                    settings={settings}
                    hasNextPage={
                      pageInfo && !disabled ? pageInfo.hasNextPage : false
                    }
                    onNextPage={onNextPage}
                    onUpdateListSettings={onUpdateListSettings}
                    hasPreviousPage={
                      pageInfo && !disabled ? pageInfo.hasPreviousPage : false
                    }
                    onPreviousPage={onPreviousPage}
                    totalCount={totalCount}
                    disabled={disabled}
                    isShowIndex
                  />
                </TableCell>
              </TableRow>
            </TableFooter>
            <TableBody>
              {noFilterInput ? (
                <TableRow>
                  <TableCell
                    colSpan={numberOfColumns}
                    className={classes.noFilteredContract}
                  >
                    <FormattedMessage defaultMessage="กรุณาเลือกตัวกรองรายการ เพื่อแสดงข้อมูล" />
                  </TableCell>
                </TableRow>
              ) : (
                renderCollection(customerContracts, (contract, index) => (
                  <TableRow
                    key={index}
                    data-testid="order-table-row"
                    hover={!!contract}
                    className={classes.cursorPointer}
                    onClick={() => onTableRowClick(contract)}
                  >
                    <TableCell className={classes.colCompany}>
                      {contract ? contract?.company?.shortName : <Skeleton />}
                    </TableCell>
                    <TableCell className={classes.colContractNo}>
                      <MyTypography variant="subtitle3">
                        {contract ? contract.code : <Skeleton />}
                      </MyTypography>
                    </TableCell>
                    <TableCell className={classes.colProjectName}>
                      {contract ? contract.projectName : <Skeleton />}
                    </TableCell>
                    <TableCell className={classes.colDate}>
                      {maybe(() => contract.startDate) ? (
                        <DateTime
                          date={contract.startDate}
                          format="DD/MM/YYYY"
                        />
                      ) : (
                        <Skeleton />
                      )}
                    </TableCell>
                    <TableCell className={classes.colDate}>
                      {maybe(() => contract.endDate) ? (
                        <DateTime date={contract.endDate} format="DD/MM/YYYY" />
                      ) : (
                        <Skeleton />
                      )}
                    </TableCell>
                    <TableCell className={classes.colPayment}>
                      {maybe(() => contract.paymentTermDescriptionTh) ? (
                        contract.paymentTermDescriptionTh
                      ) : (
                        <Skeleton />
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        ) : (
          <NotFoundContract />
        )}
        {customerContracts?.length ? (
          <StickyHorizontalScrollBar container={containerRef} />
        ) : null}
      </div>
    </>
  );
};
SoldToList.displayName = "SoldToList";
export default SoldToList;
